import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  UIEvent,
  type SyntheticEvent,
} from 'react';
import { VariableSizeList as List, FixedSizeList as FixedList } from 'react-window';
import { v4 as uuidv4 } from 'uuid';
import './Reports.scss';
import { isValidateReloadEvent } from '../helpers/validateWsHelpers';
import '../../command_center/components/center/modals/console/Console.scss';
import { getTestPlans, getSessionResult, downloadTestReport, setTelecommandResult } from '../api/validate.api';
import type { SessionResult, SessionResultItem, ReportFormat } from '../api/validate.api';
import type { TestPlan } from '../types/validateDetails.types';
import useUrlParams from '../../command_center/hooks/useUrlParams';
import useTrueTwin from '../../command_center/hooks/useTrueTwin';
import { fetchEntries } from '../../command_center/components/center/modals/console/Utils';
import { flattenValidationRows } from '../../command_center/components/center/modals/console/validationDisplayFlatten';
import { ValidationParameterLabel } from '../../command_center/components/center/modals/console/ValidationParameterLabel';
import type { ConsoleQueryFilters } from '../../command_center/components/center/modals/console/Utils';
import { getEnvVar } from '../../../envUtils';
import CommandCenterService from '../../../service/CommandCenterService';
import { CloseIcon, DownloadIcon, TcTmCombinedIcon } from '../../command_center/components/center/modals/Svgs';
import { Row as ConsoleMessageRow } from '../../command_center/components/center/modals/console/ConsoleOutputList';
import type {
  ConsoleEntry,
  OutputModalOptions,
  OutputModalSize,
  SelectedRowState,
} from '../../command_center/components/center/modals/console/types/consoleTypes';
import { Resizable } from 'react-resizable';
import type { ResizeCallbackData } from 'react-resizable';
import JsonEditor from '../../CommonComponents/json_editor/JsonEditor';
import CustomToolTip from '../../CommonComponents/CustomToolTip';
import { base64ToHex } from '../../../utils/commonFunctions';
import Loader from '../../../Layouts/Loader/Loader';
import { GreenTick, RedWarning, OrangeTimeout } from '../../CommonComponents/svgs/Svgs';
import {
  OUTPUT_MODAL_WIDTH_MAX,
  OUTPUT_MODAL_WIDTH_MIN,
} from '../../command_center/components/center/modals/console/constants/consoleConstants';

// ─── Local modules ─────────────────────────────────────────────────────────────
import type { FlatRow, GroupedSection, LeftPanelItem, LeftPanelTab, ReportsListItemData, RowResultState } from '../types/reports.types';
import { deriveIsCritical } from '../../Missions/Satellites/TestCase/TestCaseHelpers';
import { RefreshSvg, CheckIconSvg, TriangleSvg, TimeoutIconSvg } from './ReportsSvgs';
import {
  BOTTOM_LOAD_THRESHOLD,
  REPORTS_MESSAGE_COLUMNS,
  ROW_HEIGHT,
  noopGetCommandDetails,
  noopSetEntries,
  noopSetNumberState,
  noopSetTestPlanResults,
} from '../constants/reports.constants';
import {
  cellCls,
  formatDate,
  formatEpochSeconds,
  getDefaultOutputTabState,
  getCaseSelectionId,
  getPlanSelectionId,
  getReportsGroupKey,
  getSessionResultSelectionId,
  getStatusIconColor,
  getSectionSize,
  hasReportsGroupMetadata,
  mapEntryToFlatRow,
  normalizeValidationInfo,
  getValidationStatusTone,
  planMatchesSelectionId,
} from '../helpers/reports.helpers';
import { ReportsLeftSkeleton, ReportsRightBodySkeleton, ReportsRightSkeleton } from './ReportsSkeleton';
import { TestCaseSection, VirtualSection } from './ReportsSection';
import { LeftPanelCard, SubsystemChipsInline } from './ReportsLeftPanel';
import { StopWarningBanner } from './StopWarningBanner';
import { ExportMenu } from './ExportMenu';

export default function Reports() {
  const { mission_id, sat_id, scenario_id, dashboard } = useUrlParams();
  const { truetwin_id } = useTrueTwin();

  // ─── Left panel state ───────────────────────────────────────────────────────

  const [plans, setPlans] = useState<TestPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [activeLeftTab, setActiveLeftTab] = useState<LeftPanelTab>('plan');
  const [dismissedBanners, setDismissedBanners] = useState<Set<string>>(new Set());

  // ─── Entries state ──────────────────────────────────────────────────────────

  const [entries, setEntries] = useState<ConsoleEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreEntries, setHasMoreEntries] = useState(false);

  // ─── Session result state ───────────────────────────────────────────────────

  const [sessionResult, setSessionResult] = useState<SessionResult | null>(null);
  const [sessionResultRefreshing, setSessionResultRefreshing] = useState(false);

  // ─── Export menu state ──────────────────────────────────────────────────────

  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exportMenuPos, setExportMenuPos] = useState({ top: 0, right: 0 });
  const exportBtnRef = useRef<HTMLButtonElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // ─── Row selection / output modal state ────────────────────────────────────

  const [resultOverrides, setResultOverrides] = useState<Record<string, RowResultState>>({});
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [selectedConsoleEntry, setSelectedConsoleEntry] = useState<ConsoleEntry | null>(null);
  const [commandDetails, setCommandDetails] = useState<{ data?: ConsoleEntry[] } | null>(null);
  const [commandDetailsLoading, setCommandDetailsLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SelectedRowState>({
    CommandUid: '',
    selectedRow: '',
    commandIndexs: [],
    type: '',
  });
  const [hexString, setHexString] = useState('');
  const [foundPayload, setFoundPayload] = useState<Record<string, unknown> | ''>('');
  const [outputModalOptions, setOutputModalOptions] = useState<OutputModalOptions>({
    contents: false,
    json: true,
    bytes: false,
    messages: false,
    validations: false,
  });
  const [isOutputModalVisible, setIsOutputModalVisible] = useState(false);
  const [isFrozen, setIsFrozen] = useState(false);
  const [outputModalSize, setOutputModalSize] = useState<OutputModalSize>({
    width: 500,
    height: window.innerHeight - 234,
    resizing: false,
  });

  // ─── Refs ───────────────────────────────────────────────────────────────────

  const loadingRef = useRef(false);
  const listRef = useRef<List>(null);
  const messageListRef = useRef<any>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const listOuterRef = useRef<HTMLDivElement | null>(null);
  const cursorRef = useRef('');
  const isAtTopRef = useRef(true);
  const isWsLoadingRef = useRef(false);
  const isWsPendingRef = useRef(false);
  const previousSelectedPlanIdRef = useRef<string | null>(null);
  const loadLatestEntriesRef = useRef<((fromWs: boolean) => Promise<void>) | null>(null);
  const reportsPrevEntryClickRef = useRef<string | null>(null);

  const [listHeight, setListHeight] = useState(0);

  // ─── Stable card-select handler ─────────────────────────────────────────────

  const handleCardSelect = useCallback((selectionId: string) => {
    setSelectedPlanId((prev) => {
      if (prev === selectionId) return prev;
      setEntriesLoading(true);
      return selectionId;
    });
  }, []);

  // ─── Derived maps ───────────────────────────────────────────────────────────

  const planBySelectionId = useMemo(
    () => new Map(plans.map((plan) => [getPlanSelectionId(plan), plan])),
    [plans],
  );

  const caseContextBySelectionId = useMemo(() => {
    const map = new Map<string, { plan: TestPlan; testCase: TestPlan['testCases'][number] }>();
    plans.forEach((plan) => {
      plan.testCases?.forEach((testCase) => {
        map.set(getCaseSelectionId(testCase), { plan, testCase });
      });
    });
    return map;
  }, [plans]);

  // ─── Left panel items ───────────────────────────────────────────────────────
  const leftPanelItems = useMemo<LeftPanelItem[]>(() => {
    let items: LeftPanelItem[] = [];

    if (sessionResult?.results) {
      items = sessionResult.results.reduce<LeftPanelItem[]>((acc, item) => {
        const normalizedStatus = String(item.status ?? '').trim().toUpperCase();
        const rawStatusStr = String(item.rawStatus ?? item.status ?? '').trim().toUpperCase();
        const isAbortOrCancel = rawStatusStr.includes('ABORT') || rawStatusStr.includes('CANCEL') || normalizedStatus === 'CANCELLED';

        // Hide items that are in PENDING status in the Reports tab (unless manual abort / cancelled)
        if (normalizedStatus === 'PENDING' && !isAbortOrCancel) return acc;

        const selectionId = getSessionResultSelectionId(item);
        if (!selectionId) return acc;

        const planContext = item.testPlanResultId
          ? planBySelectionId.get(item.testPlanResultId)
          : undefined;
        const caseContext = item.testCaseResultId
          ? caseContextBySelectionId.get(item.testCaseResultId)
          : undefined;
        const rawTitle = item.resultType === 'TestPlan'
          ? item.testPlanName ?? planContext?.testPlanName ?? selectionId
          : item.testCaseName ?? caseContext?.testCase.testCaseName ?? selectionId;
        const fallbackDate = item.resultType === 'TestPlan'
          ? planContext?.createdAt
          : caseContext?.plan.createdAt;
        const tags = item.resultType === 'TestPlan'
          ? [item.level, item.tagType].filter((value): value is string => Boolean(value && value.trim()))
          : [];

        const fallbackStartTime = fallbackDate ? new Date(fallbackDate).getTime() / 1000 : undefined;
        const fallbackEndTime = item.resultType === 'TestPlan'
          ? planContext?.updatedAt ? new Date(planContext.updatedAt).getTime() / 1000 : undefined
          : caseContext?.plan.updatedAt ? new Date(caseContext.plan.updatedAt).getTime() / 1000 : undefined;
        const fallbackPass = item.resultType === 'TestPlan'
          ? planContext?.testCases?.reduce((count, testCase) => count + (testCase.testCaseDetails ?? []).filter((detail) => {
              const s = String(detail.testCaseEntryStatus ?? '').toUpperCase();
              return s === 'SUCCESS' || s === 'PASS' || s === 'PASSED';
            }).length, 0)
          : caseContext?.testCase.testCaseDetails?.filter((detail) => {
              const s = String(detail.testCaseEntryStatus ?? '').toUpperCase();
              return s === 'SUCCESS' || s === 'PASS' || s === 'PASSED';
            }).length;
        const fallbackFail = item.resultType === 'TestPlan'
          ? planContext?.testCases?.reduce((count, testCase) => count + (testCase.testCaseDetails ?? []).filter((detail) => {
              const s = String(detail.testCaseEntryStatus ?? '').toUpperCase();
              return s === 'FAILED' || s === 'FAIL';
            }).length, 0)
          : caseContext?.testCase.testCaseDetails?.filter((detail) => {
              const s = String(detail.testCaseEntryStatus ?? '').toUpperCase();
              return s === 'FAILED' || s === 'FAIL';
            }).length;
        const fallbackTimeout = item.resultType === 'TestPlan'
          ? planContext?.testCases?.reduce((count, testCase) => count + (testCase.testCaseDetails ?? []).filter((detail) => {
              const s = String(detail.testCaseEntryStatus ?? '').toUpperCase();
              return s === 'TIMEOUT' || s === 'TIMED_OUT' || s === 'TIMEDOUT';
            }).length, 0)
          : caseContext?.testCase.testCaseDetails?.filter((detail) => {
              const s = String(detail.testCaseEntryStatus ?? '').toUpperCase();
              return s === 'TIMEOUT' || s === 'TIMED_OUT' || s === 'TIMEDOUT';
            }).length;

        const isCritical = item.resultType === 'TestPlan'
          ? !!planContext?.isCritical || !!item.isCritical
          : deriveIsCritical(caseContext?.testCase) || !!item.isCritical;

        const pass = item.pass !== undefined ? item.pass : (fallbackPass !== undefined ? fallbackPass : 0);
        const fail = item.fail !== undefined ? item.fail : (fallbackFail !== undefined ? fallbackFail : 0);

        let resolvedTimeout = 0;
        if (item.resultType === 'TestPlan') {
          if (item.timeout !== undefined && item.timeout > 0) {
            resolvedTimeout = item.timeout;
          } else {
            // Count matching test cases from sessionResult.results that timed out
            const matchingTimeouts = sessionResult.results.filter((res) => {
              if (res.resultType !== 'TestCase') return false;
              const matchesPlan =
                (item.testPlanResultId && res.testPlanResultId === item.testPlanResultId) ||
                (item.testPlanId && res.testPlanId === item.testPlanId);
              return matchesPlan && String(res.status ?? '').trim().toUpperCase() === 'TIMEOUT';
            }).length;

            if (matchingTimeouts > 0) {
              resolvedTimeout = matchingTimeouts;
            } else if (fallbackTimeout !== undefined && fallbackTimeout > 0) {
              resolvedTimeout = fallbackTimeout;
            } else {
              resolvedTimeout = item.timeout ?? 0;
            }
          }
        } else {
          // TestCase
          if (String(item.status ?? '').trim().toUpperCase() === 'TIMEOUT') {
            resolvedTimeout = item.timeout !== undefined && item.timeout > 0 ? item.timeout : 1;
          } else if (item.timeout !== undefined) {
            resolvedTimeout = item.timeout;
          } else if (fallbackTimeout !== undefined && fallbackTimeout > 0) {
            resolvedTimeout = fallbackTimeout;
          } else {
            resolvedTimeout = 0;
          }
        }

        acc.push({
          key: `${item.resultType.toLowerCase()}-${selectionId}`,
          type: item.resultType === 'TestPlan' ? 'plan' : 'case',
          selectionId,
          rawTitle,
          meta: formatEpochSeconds(item.startTime) ?? (fallbackDate ? formatDate(fallbackDate) : undefined),
          status: item.status,
          rawStatus: item.rawStatus,
          tags,
          subsystems: item.subsystems ?? [],
          startTime: item.startTime ?? fallbackStartTime,
          endTime: item.endTime ?? fallbackEndTime,
          pass,
          fail,
          timeout: resolvedTimeout,
          duration: item.duration?.toString(),
          isCritical,
          stopInfo: item.stopInfo,
        });

        return acc;
      }, []);
    } else {
      items = plans.flatMap((plan) => {
        const planSelectionId = getPlanSelectionId(plan);
        const planSummary = plan as { tags?: string[]; pass?: unknown; fail?: unknown; duration?: unknown; durationMinutes?: unknown };
        const planPassFallback = (plan.testCases ?? []).reduce(
          (count, testCase) => count + (testCase.testCaseDetails ?? []).filter((d) => {
            const s = String(d.testCaseEntryStatus ?? '').toUpperCase();
            return s === 'SUCCESS' || s === 'PASS' || s === 'PASSED';
          }).length,
          0,
        );
        const planFailFallback = (plan.testCases ?? []).reduce(
          (count, testCase) => count + (testCase.testCaseDetails ?? []).filter((d) => {
            const s = String(d.testCaseEntryStatus ?? '').toUpperCase();
            return s === 'FAILED' || s === 'FAIL';
          }).length,
          0,
        );
        const planTimeoutFallback = (plan.testCases ?? []).reduce(
          (count, testCase) => count + (testCase.testCaseDetails ?? []).filter((d) => {
            const s = String(d.testCaseEntryStatus ?? '').toUpperCase();
            return s === 'TIMEOUT' || s === 'TIMED_OUT' || s === 'TIMEDOUT';
          }).length,
          0,
        );

        const planItem: LeftPanelItem = {
          key: `plan-${planSelectionId}`,
          type: 'plan',
          selectionId: planSelectionId,
          rawTitle: plan.testPlanName,
          meta: formatDate(plan.createdAt),
          status: null,
          tags: (planSummary.tags ?? []).slice(0, 2),
          subsystems: [],
          startTime: plan.createdAt ? new Date(plan.createdAt).getTime() / 1000 : undefined,
          endTime: plan.updatedAt ? new Date(plan.updatedAt).getTime() / 1000 : undefined,
          pass: planPassFallback,
          fail: planFailFallback,
          timeout: planTimeoutFallback > 0 ? planTimeoutFallback : 0,
          duration: ((planSummary.duration ?? planSummary.durationMinutes) as string | number | undefined)?.toString(),
          isCritical: !!plan.isCritical,
        };

        const caseItems: LeftPanelItem[] = (plan.testCases ?? []).map((testCase) => {
          const testCaseSummary = testCase as { pass?: unknown; fail?: unknown; timeout?: unknown; duration?: unknown; durationMinutes?: unknown };
          const testCasePassFallback = (testCase.testCaseDetails ?? []).filter((d) => {
            const s = String(d.testCaseEntryStatus ?? '').toUpperCase();
            return s === 'SUCCESS' || s === 'PASS' || s === 'PASSED';
          }).length;
          const testCaseFailFallback = (testCase.testCaseDetails ?? []).filter((d) => {
            const s = String(d.testCaseEntryStatus ?? '').toUpperCase();
            return s === 'FAILED' || s === 'FAIL';
          }).length;
          const testCaseTimeoutFallback = (testCase.testCaseDetails ?? []).filter((d) => {
            const s = String(d.testCaseEntryStatus ?? '').toUpperCase();
            return s === 'TIMEOUT' || s === 'TIMED_OUT' || s === 'TIMEDOUT';
          }).length;

          return {
            key: `case-${getCaseSelectionId(testCase)}`,
            type: 'case',
            selectionId: getCaseSelectionId(testCase),
            rawTitle: testCase.testCaseName,
            meta: formatDate(plan.createdAt),
            status: null,
            subsystems: [],
            tags: [],
            startTime: plan.createdAt ? new Date(plan.createdAt).getTime() / 1000 : undefined,
            endTime: plan.updatedAt ? new Date(plan.updatedAt).getTime() / 1000 : undefined,
            pass: testCasePassFallback,
            fail: testCaseFailFallback,
            timeout: testCaseTimeoutFallback > 0 ? testCaseTimeoutFallback : 0,
            duration: ((testCaseSummary.duration ?? testCaseSummary.durationMinutes) as string | number | undefined)?.toString(),
            isCritical: deriveIsCritical(testCase),
          };
        });

        return [planItem, ...caseItems];
      });
    }

    // Post-processing to assign runIndex to duplicate runs
    const groups: { [key: string]: LeftPanelItem[] } = {};
    items.forEach((item) => {
      const key = `${item.type}-${item.rawTitle.toLowerCase().trim()}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
    });

    Object.keys(groups).forEach((key) => {
      const groupItems = groups[key];
      if (groupItems.length > 1) {
        groupItems.sort((a, b) => (a.startTime ?? 0) - (b.startTime ?? 0));
        groupItems.forEach((item, idx) => {
          (item as any).runIndex = idx + 1;
        });
      }
    });

    return items;
  }, [caseContextBySelectionId, planBySelectionId, plans, sessionResult]);
  const leftPanelCounts = useMemo(
    () => leftPanelItems.reduce(
      (acc, item) => {
        if (item.type === 'plan') acc.plan += 1;
        if (item.type === 'case') acc.case += 1;
        return acc;
      },
      { plan: 0, case: 0 },
    ),
    [leftPanelItems],
  );

  const latestRequestIdRef = useRef(0);

  const visibleLeftPanelItems = useMemo(
    () => leftPanelItems.filter((item) => item.type === activeLeftTab),
    [activeLeftTab, leftPanelItems],
  );

  const handleLeftTabChange = useCallback((tab: 'plan' | 'case') => {
    setActiveLeftTab(tab);
    const itemsForTab = leftPanelItems.filter((item) => item.type === tab);
    if (itemsForTab.length > 0) {
      setSelectedPlanId(itemsForTab[0].selectionId);
    }
  }, [leftPanelItems]);

  const effectiveSelectedPlanId = useMemo(() => {
    if (visibleLeftPanelItems.length === 0) return null;
    const directMatch = visibleLeftPanelItems.find((item) => item.selectionId === selectedPlanId);
    if (directMatch) return selectedPlanId;

    const matchedResult = sessionResult?.results.find((item) => {
      const itemTabType = item.resultType === 'TestPlan' ? 'plan' : 'case';
      if (itemTabType !== activeLeftTab) return false;
      const normalizedStatus = String(item.status ?? '').trim().toUpperCase();
      const rawStatusStr = String(item.rawStatus ?? item.status ?? '').trim().toUpperCase();
      const isAbortOrCancel = rawStatusStr.includes('ABORT') || rawStatusStr.includes('CANCEL') || normalizedStatus === 'CANCELLED';
      if (normalizedStatus === 'PENDING' && !isAbortOrCancel) return false;
      return (
        item.testPlanId === selectedPlanId ||
        item.testCaseId === selectedPlanId ||
        item.testPlanResultId === selectedPlanId ||
        item.testCaseResultId === selectedPlanId
      );
    });
    if (matchedResult) {
      return getSessionResultSelectionId(matchedResult);
    }

    return visibleLeftPanelItems[0]?.selectionId ?? null;
  }, [selectedPlanId, visibleLeftPanelItems, sessionResult?.results, activeLeftTab]);

  useEffect(() => {
    if (effectiveSelectedPlanId && effectiveSelectedPlanId !== selectedPlanId) {
      setSelectedPlanId(effectiveSelectedPlanId);
    }
  }, [effectiveSelectedPlanId, selectedPlanId]);

  const selectedTimeRange = useMemo(() => {
    const targetId = effectiveSelectedPlanId;
    if (!targetId) return null;
    const selectedItem = visibleLeftPanelItems.find((item) => item.selectionId === targetId) ?? visibleLeftPanelItems[0];
    if (!selectedItem || selectedItem.startTime === undefined) return null;
    return { startTime: selectedItem.startTime, endTime: selectedItem.endTime ?? -1 };
  }, [effectiveSelectedPlanId, visibleLeftPanelItems]);

  const queryFilters = useMemo<ConsoleQueryFilters>(() => {
    const targetId = effectiveSelectedPlanId;
    if (!targetId) return {};

    const selectedItem =
      visibleLeftPanelItems.find((item) => item.selectionId === targetId) ??
      leftPanelItems.find((item) => item.selectionId === targetId) ??
      visibleLeftPanelItems[0];

    const selectedResult = sessionResult?.results.find(
      (item) =>
        getSessionResultSelectionId(item) === targetId ||
        item.testPlanResultId === targetId ||
        item.testPlanId === targetId ||
        item.testCaseResultId === targetId ||
        item.testCaseId === targetId
    );

    const validateSelectionFilter: NonNullable<ConsoleQueryFilters['validateSelectionFilter']> = {};
    const isPlan = activeLeftTab === 'plan';
    const isCase = activeLeftTab === 'case';

    if (isPlan) {
      const planResId = selectedResult?.testPlanResultId ?? selectedItem?.selectionId ?? targetId;
      if (planResId && !planResId.startsWith('prepopulated-')) {
        validateSelectionFilter.testPlanResultId = planResId;
      } else {
        validateSelectionFilter.testPlanId = targetId;
      }
    } else if (isCase) {
      const caseResId = selectedResult?.testCaseResultId ?? selectedItem?.selectionId ?? targetId;
      if (caseResId && !caseResId.startsWith('prepopulated-')) {
        validateSelectionFilter.testCaseResultId = caseResId;
      } else {
        validateSelectionFilter.testCaseId = targetId;
      }
    }

    const fromTime = selectedTimeRange?.startTime ?? selectedItem?.startTime;
    const toTime = selectedTimeRange?.endTime ?? selectedItem?.endTime ?? -1;
    const shouldApplyGroundstationTimeFilter = fromTime != null && toTime != null && toTime !== -1;

    return {
      timeFilter: fromTime != null ? { startTime: fromTime, endTime: toTime } : undefined,
      validateSelectionFilter,
      payloadOverrides: {
        TMHeaderTimestampRange: { IsFilterApplied: false, FromTime: -1, ToTime: -1 },
        GSTimestampRange: {
          IsFilterApplied: shouldApplyGroundstationTimeFilter,
          FromTime: shouldApplyGroundstationTimeFilter ? fromTime : -1,
          ToTime: shouldApplyGroundstationTimeFilter ? toTime : -1,
        },
      },
    };
  }, [effectiveSelectedPlanId, selectedTimeRange, sessionResult?.results, visibleLeftPanelItems, leftPanelItems]);

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const fetchPlans = useCallback(async () => {
    if (!mission_id || !sat_id) {
      setPlans([]);
      setSelectedPlanId(null);
      setPlansLoading(false);
      return;
    }
    setPlansLoading(true);
    try {
      const { data } = await getTestPlans(mission_id, sat_id);
      setPlans(data);
      setSelectedPlanId((prev) => {
        if (data.length === 0) return null;
        return data.some((plan) => planMatchesSelectionId(plan, prev)) ? prev : getPlanSelectionId(data[0]);
      });
    } finally {
      setPlansLoading(false);
    }
  }, [mission_id, sat_id]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const refreshSessionResult = useCallback(async () => {
    if (!mission_id || !scenario_id) return;
    setSessionResultRefreshing(true);
    try {
      const { data } = await getSessionResult(mission_id, scenario_id);
      if (data) setSessionResult(data);
    } finally {
      setSessionResultRefreshing(false);
    }
  }, [mission_id, scenario_id]);

  useEffect(() => { refreshSessionResult(); }, [refreshSessionResult]);

  const loadLatestEntries = useCallback(async (fromWs: boolean) => {
    const targetId = effectiveSelectedPlanId ?? selectedPlanId;
    if (!sessionResult || sessionResultRefreshing || !targetId || !sat_id || !scenario_id || !truetwin_id) {
      if (!sessionResultRefreshing && !sessionResult) {
        setEntries([]);
        setHasMoreEntries(false);
        setEntriesLoading(false);
        cursorRef.current = '';
      }
      return;
    }

    const currentRequestId = ++latestRequestIdRef.current;

    if (fromWs && isWsLoadingRef.current) {
      isWsPendingRef.current = true;
      return;
    }

    loadingRef.current = true;
    if (fromWs) {
      isWsLoadingRef.current = true;
    } else {
      setEntriesLoading(true);
    }

    try {
      const { data, cursor } = await fetchEntries(
        sat_id ?? '',
        truetwin_id ?? '',
        scenario_id ?? '',
        '',
        queryFilters,
        false,
        setEntriesLoading,
        false,
      );

      if (currentRequestId !== latestRequestIdRef.current) {
        return;
      }

      setEntries(data as ConsoleEntry[]);
      cursorRef.current = cursor;
      setHasMoreEntries(Boolean(cursor));
    } finally {
      loadingRef.current = false;
      if (fromWs) {
        isWsLoadingRef.current = false;
        if (isWsPendingRef.current) {
          isWsPendingRef.current = false;
          loadLatestEntries(true);
        }
      } else {
        setEntriesLoading(false);
      }
    }
  }, [sat_id, truetwin_id, scenario_id, selectedPlanId, selectedTimeRange, queryFilters, plans, sessionResult, sessionResultRefreshing]);

  const loadMoreEntries = useCallback(async () => {
    if (loadingRef.current || !cursorRef.current || !selectedPlanId || !selectedTimeRange || !sat_id || !scenario_id || !truetwin_id) return;

    loadingRef.current = true;
    setLoadingMore(true);
    try {
      const { data, cursor } = await fetchEntries(
        sat_id ?? '',
        truetwin_id ?? '',
        scenario_id ?? '',
        cursorRef.current,
        queryFilters,
        false,
        setEntriesLoading,
        true,
      );
      setEntries((prev) => [...prev, ...(data as ConsoleEntry[])]);
      cursorRef.current = cursor;
      setHasMoreEntries(Boolean(cursor));
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, [sat_id, truetwin_id, scenario_id, selectedPlanId, selectedTimeRange, queryFilters]);

  useEffect(() => { loadLatestEntriesRef.current = loadLatestEntries; });

  useEffect(() => {
    if (plansLoading || sessionResultRefreshing || !sessionResult) return;
    setEntries([]);
    setEntriesLoading(true);
    setHasMoreEntries(false);
    cursorRef.current = '';
    isAtTopRef.current = true;
    listRef.current?.scrollTo(0);
    const targetId = effectiveSelectedPlanId ?? selectedPlanId;
    if (!targetId) { setEntriesLoading(false); return; }
    loadLatestEntriesRef.current?.(false);
  }, [plansLoading, sessionResultRefreshing, sessionResult, selectedPlanId, effectiveSelectedPlanId, activeLeftTab]);

  useEffect(() => {
    const token = sessionStorage.getItem('authentication');
    if (!truetwin_id || !token) return;

    const sessionId = uuidv4();
    const ws = new WebSocket(
      `${getEnvVar('REACT_APP_WEBSOCKET_BACKEND_BASE_URL')}groundstation/websocketstatus?truetwinId=${truetwin_id}&sessionId=${sessionId}`,
    );

    ws.onopen = () => ws.send(JSON.stringify({ type: 'authorization', token }));
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data as string);
      const isReloadEvent = isValidateReloadEvent(msg);
      if (isReloadEvent && isAtTopRef.current && selectedPlanId) {
        loadLatestEntries(true);
      }
    };
    ws.onerror = console.error;
    return () => ws.close();
  }, [truetwin_id, loadLatestEntries, selectedPlanId]);

  // ─── List container height ──────────────────────────────────────────────────

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const updateHeight = () => {
      const outputModalEl = container.parentElement?.querySelector('.output__modal') as HTMLDivElement | null;
      const modalHeight = isOutputModalVisible && outputModalEl ? outputModalEl.clientHeight : 0;
      setListHeight(Math.max(0, container.clientHeight - modalHeight));
    };

    updateHeight();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateHeight);
      observer.observe(container);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [isOutputModalVisible]);

  // ─── Derived selection ──────────────────────────────────────────────────────

  const selectedPlan = useMemo(
    () => plans.find((plan) => planMatchesSelectionId(plan, selectedPlanId)) ?? null,
    [plans, selectedPlanId],
  );

  const selectedTestCase = useMemo(
    () => selectedPlan?.testCases?.find((testCase) => getCaseSelectionId(testCase) === selectedPlanId) ?? null,
    [selectedPlan, selectedPlanId],
  );

  const selectedSessionResultItem = useMemo(
    () => sessionResult?.results.find((item) => getSessionResultSelectionId(item) === selectedPlanId) ?? null,
    [selectedPlanId, sessionResult],
  );

  const selectedPlanResultId = useMemo(() => {
    if (selectedPlan) return getPlanSelectionId(selectedPlan);
    if (selectedSessionResultItem?.resultType === 'TestPlan') {
      return getSessionResultSelectionId(selectedSessionResultItem) || null;
    }
    return selectedSessionResultItem?.testPlanResultId ?? null;
  }, [selectedPlan, selectedSessionResultItem]);

  const selectedTestCaseId = useMemo(() => {
    if (selectedTestCase) return getCaseSelectionId(selectedTestCase);
    if (selectedSessionResultItem?.resultType === 'TestCase') {
      return getSessionResultSelectionId(selectedSessionResultItem) || null;
    }
    return null;
  }, [selectedSessionResultItem, selectedTestCase]);

  const selectedReportItemId = selectedTestCaseId ?? selectedPlanResultId ?? null;

  const filteredEntries = useMemo(
    () => (!selectedReportItemId ? [] : entries),
    [entries, selectedReportItemId],
  );

  const allFlatRows = useMemo(
    () => filteredEntries.map((entry, index) => mapEntryToFlatRow(entry, index)).filter((row): row is FlatRow => row !== null),
    [filteredEntries],
  );

  // ─── Command details ────────────────────────────────────────────────────────

  const selectedCommandEntries = useMemo(() => {
    const apiEntries = Array.isArray(commandDetails?.data) ? commandDetails.data : [];
    if (apiEntries.length > 0) return apiEntries;
    if (!selectedConsoleEntry) return [];

    const selectedCommandUid = String(selectedConsoleEntry.CommandUid ?? '').trim();
    if (selectedCommandUid !== '') {
      const relatedEntries = filteredEntries.filter(
        (entry) => String(entry.CommandUid ?? '').trim() === selectedCommandUid,
      );
      if (relatedEntries.length > 0) return relatedEntries;
    }
    return [selectedConsoleEntry];
  }, [commandDetails?.data, filteredEntries, selectedConsoleEntry]);

  const selectedFlatRow = useMemo(
    () => (!selectedRowId ? null : allFlatRows.find((row) => row.uniqueEntryId === selectedRowId) ?? null),
    [allFlatRows, selectedRowId],
  );

  const selectedDetailedEntry = useMemo(() => {
    if (!selectedFlatRow?.entry) return null;
    return (
      commandDetails?.data?.find((entry) => entry?.type === (selectedFlatRow.entry.type ?? selectedRow.type))
      ?? selectedFlatRow.entry
    );
  }, [commandDetails?.data, selectedFlatRow?.entry, selectedRow.type]);

  const validationRows = useMemo(() => normalizeValidationInfo(
    selectedDetailedEntry?.ValidationDetails
    ?? selectedDetailedEntry?.CommandDetails?.ValidationDetails
    ?? selectedDetailedEntry?.ValidationInfo
    ?? selectedDetailedEntry?.CommandDetails?.ValidationInfo,
  ), [selectedDetailedEntry]);

  const validationDisplayRows = useMemo(() => flattenValidationRows(validationRows), [validationRows]);

  const selectedValidationResult = useMemo(() => {
    const result = selectedFlatRow ? (resultOverrides[selectedFlatRow.rowId] ?? selectedFlatRow.result) : 'pending';
    if (result === 'pass') return true;
    if (result === 'fail') return false;
    return undefined;
  }, [resultOverrides, selectedFlatRow]);

  const isSelectedRowValidationAllowed = selectedFlatRow?.entry?.isValidationAllowed ?? false;

  const showNoMessage = Boolean(selectedConsoleEntry) &&
    !selectedCommandEntries.some((entry) => entry?.type !== selectedRow.type);

  // ─── Row interaction handlers ───────────────────────────────────────────────

  const getCommandDetails = useCallback(async (entry: ConsoleEntry) => {
    if (!truetwin_id || !sat_id || !scenario_id || !entry?.CommandUid) {
      setCommandDetails(null);
      return;
    }
    setCommandDetailsLoading(true);
    try {
      const response = await CommandCenterService.getCommandDetailsById(
        truetwin_id,
        sat_id,
        scenario_id,
        entry.CommandUid,
      );
      setCommandDetails({ data: Array.isArray(response?.data) ? response.data : [] });
    } catch (error) {
      console.error('Failed to get command details', error);
      setCommandDetails(null);
    } finally {
      setCommandDetailsLoading(false);
    }
  }, [sat_id, scenario_id, truetwin_id]);

  const updateHexAndPayload = useCallback((entry: ConsoleEntry | null) => {
    if (!entry) { setHexString(''); setFoundPayload(''); return; }

    let base64String = entry.CommandDetails?.completeFrame ?? '';
    let payload = { ...(entry.CommandDetails ?? {}) };
    const matchingApiEntry = commandDetails?.data?.find(
      (commandEntry) => commandEntry?.type === (entry.type ?? selectedRow.type),
    );

    if (matchingApiEntry?.CommandDetails) {
      base64String = matchingApiEntry.CommandDetails.completeFrame ?? '';
      payload = { ...matchingApiEntry.CommandDetails };
    }

    delete payload.payloadBytes;
    delete payload.completeFrame;
    setHexString(base64ToHex(base64String));
    setFoundPayload(payload);
  }, [commandDetails?.data, selectedRow.type]);

  const showSelectedOutputDetails = useCallback(
    (entry: ConsoleEntry | null, entriesList: ConsoleEntry[]) => {
      if (!entry) {
        setSelectedConsoleEntry(null);
        setSelectedRowId(null);
        setSelectedRow({ CommandUid: '', selectedRow: '', commandIndexs: [], type: '' });
        setHexString('');
        setFoundPayload('');
        setOutputModalOptions(getDefaultOutputTabState(null));
        return;
      }

      const positions = entriesList.reduce<number[]>((acc, currentEntry, index) => {
        if (currentEntry.CommandUid === entry.CommandUid) acc.push(index);
        return acc;
      }, []);

      const entryUniqueId = String(entry.uniqueId ?? entry.CommandUid ?? '');
      const selected_row = entriesList.findIndex(
        (currentEntry) => String(currentEntry.uniqueId ?? currentEntry.CommandUid ?? '') === entryUniqueId,
      );
      const already_selected = selected_row === selectedRow.selectedRow;

      if (already_selected) {
        setSelectedConsoleEntry(null);
        setSelectedRowId(null);
        setSelectedRow({ CommandUid: '', selectedRow: '', commandIndexs: [], type: '' });
        setHexString('');
        setFoundPayload('');
        setOutputModalOptions(getDefaultOutputTabState(null));
        return;
      }

      setSelectedConsoleEntry(entry);
      setSelectedRowId(entryUniqueId || null);
      setSelectedRow({
        CommandUid: entryUniqueId,
        selectedRow: selected_row >= 0 ? selected_row : '',
        commandIndexs: positions,
        type: entry.type ?? '',
      });
      setOutputModalOptions(getDefaultOutputTabState(entry));
      updateHexAndPayload(entry);
    },
    [selectedRow.selectedRow, updateHexAndPayload],
  );

  const removeSelection = useCallback(() => {
    setIsOutputModalVisible(false);
    setIsFrozen(false);
    setSelectedConsoleEntry(null);
    setCommandDetails(null);
    setCommandDetailsLoading(false);
    setSelectedRowId(null);
    setSelectedRow({ CommandUid: '', selectedRow: '', commandIndexs: [], type: '' });
    setHexString('');
    setFoundPayload('');
    setOutputModalOptions(getDefaultOutputTabState(null));
    reportsPrevEntryClickRef.current = null;
  }, []);

  const keepSelectedRowVisible = useCallback(() => {
    if (!isOutputModalVisible || !selectedRowId) return;

    const scrollElement = listOuterRef.current
      ?? listContainerRef.current?.querySelector('.rp__scroll') as HTMLDivElement | null;
    const selectedElement = listContainerRef.current?.querySelector('.rp__row--active') as HTMLDivElement | null;
    if (!scrollElement || !selectedElement) return;

    const scrollRect = scrollElement.getBoundingClientRect();
    const rowRect = selectedElement.getBoundingClientRect();
    const outputModalElement = listContainerRef.current?.parentElement?.querySelector('.output__modal') as HTMLDivElement | null;

    let visibleBottom = scrollRect.bottom - 12;
    if (outputModalElement) {
      const modalRect = outputModalElement.getBoundingClientRect();
      if (modalRect.top < scrollRect.bottom && modalRect.bottom > scrollRect.top) {
        visibleBottom = Math.min(visibleBottom, modalRect.top - 12);
      }
    }

    const viewportHeight = scrollElement.clientHeight || listHeight;
    const preferredTopOffset = Math.max(16, Math.floor(viewportHeight * 0.18));
    const preferredBottomBoundary = Math.floor(viewportHeight * 0.42);
    const currentScrollTop = scrollElement.scrollTop;
    const relativeRowTop = rowRect.top - scrollRect.top;
    const rowTop = currentScrollTop + relativeRowTop;
    let nextScrollTop = currentScrollTop;

    if (rowRect.top < scrollRect.top + 16) {
      nextScrollTop = Math.max(0, rowTop - 16);
    } else if (relativeRowTop > preferredBottomBoundary || rowRect.bottom > visibleBottom) {
      nextScrollTop = Math.max(0, rowTop - preferredTopOffset);
    }

    if (Math.abs(nextScrollTop - currentScrollTop) > 1) {
      scrollElement.scrollTo({ top: nextScrollTop, behavior: 'smooth' });
    }
  }, [isOutputModalVisible, listHeight, selectedRowId]);

  const handleSelectRow = useCallback((row: FlatRow) => {
    const entry = row.entry;
    if (!entry) return;

    const uid = row.uniqueEntryId;
    if (reportsPrevEntryClickRef.current !== uid) {
      setIsOutputModalVisible(true);
      setIsFrozen(true);
    } else {
      setIsOutputModalVisible((v) => !v);
      setIsFrozen((v) => !v);
    }

    getCommandDetails(entry);
    showSelectedOutputDetails(entry, filteredEntries);
    reportsPrevEntryClickRef.current = uid;
  }, [filteredEntries, getCommandDetails, showSelectedOutputDetails]);

  useEffect(() => {
    if (!isOutputModalVisible || !selectedRowId) return;
    let frameOne = 0;
    let frameTwo = 0;
    frameOne = requestAnimationFrame(() => {
      frameTwo = requestAnimationFrame(() => { keepSelectedRowVisible(); });
    });
    return () => { cancelAnimationFrame(frameOne); cancelAnimationFrame(frameTwo); };
  }, [isOutputModalVisible, keepSelectedRowVisible, selectedRowId]);

  // ─── Output modal tab / resize handlers ────────────────────────────────────

  const handleTabChange = useCallback((selectedKey: keyof OutputModalOptions) => {
    setOutputModalOptions({
      contents: false, json: false, bytes: false, messages: false, validations: false,
      [selectedKey]: true,
    });
  }, []);

  const downloadOutputDetails = useCallback(() => {
    if (!hexString || !foundPayload) return;
    const data = { 'Packet Details': foundPayload, 'Raw Bytes': hexString };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const element = document.createElement('a');
    element.href = URL.createObjectURL(blob);
    element.download = `output_details_${selectedConsoleEntry?.CommandUid ?? 'data'}.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(element.href);
  }, [foundPayload, hexString, selectedConsoleEntry?.CommandUid]);

  const onResizeWidth = useCallback(
    (event: SyntheticEvent, { size }: { node: HTMLElement; size: { width: number }; handle: string }) => {
      event.stopPropagation();
      if (size.width >= OUTPUT_MODAL_WIDTH_MIN && size.width <= OUTPUT_MODAL_WIDTH_MAX) {
        setOutputModalSize((prev) => ({ ...prev, width: size.width, resizing: 'width' }));
      }
    },
    [],
  );

  const onResizeStop = useCallback((event: SyntheticEvent, _data: ResizeCallbackData) => {
    event.stopPropagation();
    setOutputModalSize((prev) => ({ ...prev, resizing: false }));
  }, []);

  const onResizeStart = useCallback((event: SyntheticEvent, _data: ResizeCallbackData) => {
    event.stopPropagation();
    setOutputModalSize((prev) => ({ ...prev, resizing: true }));
  }, []);

  useEffect(() => {
    const updateHeight = () => setOutputModalSize((prev) => ({ ...prev, height: window.innerHeight - 234 }));
    window.addEventListener('resize', updateHeight);
    updateHeight();
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  useEffect(() => {
    if (!selectedConsoleEntry || !commandDetails) return;
    updateHexAndPayload(selectedConsoleEntry);
  }, [commandDetails, selectedConsoleEntry, updateHexAndPayload]);

  // ─── Plan change side effects ───────────────────────────────────────────────

  useEffect(() => {
    const planChanged = previousSelectedPlanIdRef.current !== selectedPlanId;
    previousSelectedPlanIdRef.current = selectedPlanId;
    if (planChanged) {
      setResultOverrides({});
      removeSelection();
      listRef.current?.scrollTo(0);
    }
  }, [removeSelection, selectedPlanId]);

  useEffect(() => {
    if (!selectedConsoleEntry) return;
    const stillPresent = filteredEntries.some(
      (entry) =>
        String(entry.uniqueId ?? entry.CommandUid ?? '') ===
        String(selectedConsoleEntry.uniqueId ?? selectedConsoleEntry.CommandUid ?? ''),
    );
    if (!stillPresent) removeSelection();
  }, [filteredEntries, removeSelection, selectedConsoleEntry]);

  useEffect(() => {
    if (!selectedReportItemId || entriesLoading || loadingMore || loadingRef.current) return;
    if (entries.length === 0) return;
    if (allFlatRows.length === 0 && hasMoreEntries) { loadMoreEntries(); }
  }, [selectedReportItemId, entries.length, entriesLoading, loadingMore, allFlatRows.length, hasMoreEntries, loadMoreEntries]);

  // ─── Grouped rows ───────────────────────────────────────────────────────────

  const groupedRows = useMemo(() => {
    const groups: GroupedSection[] = [];

    for (const row of allFlatRows) {
      const rowWithOverride = { ...row, result: resultOverrides[row.rowId] ?? row.result };
      const groupKey = activeLeftTab === 'case'
        ? (row.testCaseName || row.testCaseResultId || row.testCaseId || 'single-case-group')
        : getReportsGroupKey(row);
      const lastGroup = groups[groups.length - 1];

      if (groupKey === '') {
        if (lastGroup && !lastGroup.hasGroupingMeta) {
          lastGroup.rows.push(rowWithOverride);
          continue;
        }
        groups.push({
          groupKey: `ungrouped::${groups.length}`,
          testPlanResultId: undefined,
          testCaseId: row.testCaseId,
          testCaseResultId: undefined,
          testPlanName: undefined,
          testCaseName: undefined,
          hasGroupingMeta: false,
          rows: [rowWithOverride],
        });
        continue;
      }

      if (lastGroup && lastGroup.hasGroupingMeta && lastGroup.groupKey === groupKey) {
        lastGroup.rows.push(rowWithOverride);
        continue;
      }

      groups.push({
        groupKey,
        testPlanResultId: row.testPlanResultId,
        testCaseId: row.testCaseId,
        testCaseResultId: row.testCaseResultId,
        testPlanName: row.testPlanName,
        testCaseName: row.testCaseName,
        hasGroupingMeta: true,
        rows: [rowWithOverride],
      });
    }

    return groups;
  }, [allFlatRows, resultOverrides, activeLeftTab]);

  // ─── Stats ──────────────────────────────────────────────────────────────────

  const planStats = useMemo(() => plans.map((plan) => {
    let pass = 0; let fail = 0; let timeout = 0; let total = 0;
    for (const testCase of plan.testCases ?? []) {
      for (const detail of testCase.testCaseDetails ?? []) {
        total++;
        const s = String(detail.testCaseEntryStatus ?? '').toUpperCase();
        if (s === 'SUCCESS' || s === 'PASS' || s === 'PASSED') pass++;
        else if (s === 'FAILED' || s === 'FAIL') fail++;
        else if (s === 'TIMEOUT' || s === 'TIMED_OUT' || s === 'TIMEDOUT') timeout++;
      }
    }
    return { planId: plan.testPlanId, pass, fail, timeout, total };
  }), [plans]);

  const totalStats = useMemo(
    () => planStats.reduce(
      (acc, stat) => ({
        total: acc.total + stat.total,
        pass: acc.pass + stat.pass,
        fail: acc.fail + stat.fail,
        timeout: acc.timeout + stat.timeout,
      }),
      { total: 0, pass: 0, fail: 0, timeout: 0 },
    ),
    [planStats],
  );

  // ─── Tab auto-selection ─────────────────────────────────────────────────────

  useEffect(() => {
    if (leftPanelCounts.plan === 0 && leftPanelCounts.case === 0) return;
    setActiveLeftTab((prev) => {
      if (prev === 'plan' && leftPanelCounts.plan > 0) return prev;
      if (prev === 'case' && leftPanelCounts.case > 0) return prev;
      return leftPanelCounts.plan > 0 ? 'plan' : 'case';
    });
  }, [leftPanelCounts.case, leftPanelCounts.plan]);

  useEffect(() => {
    setSelectedPlanId((prev) => {
      if (visibleLeftPanelItems.length === 0) return null;
      if (prev && visibleLeftPanelItems.some((item) => item.selectionId === prev)) return prev;
      return visibleLeftPanelItems[0].selectionId;
    });
  }, [visibleLeftPanelItems]);

  useEffect(() => { listRef.current?.resetAfterIndex(0); }, [groupedRows]);

  // ─── Header / mark handlers ─────────────────────────────────────────────────

  const selectedLeftPanelItem = useMemo(
    () => leftPanelItems.find((item) => item.selectionId === selectedPlanId) ?? null,
    [leftPanelItems, selectedPlanId],
  );



  const selectedHeaderTitle = useMemo(() => {
    if (selectedPlan && selectedTestCase) return `${selectedPlan.testPlanName} | ${selectedTestCase.testCaseName}`;
    if (selectedPlan) return selectedPlan.testPlanName;
    if (selectedLeftPanelItem) return selectedLeftPanelItem.rawTitle;
    return 'Select a test plan.';
  }, [selectedLeftPanelItem, selectedPlan, selectedTestCase]);

  const handleMarkRow = useCallback((rowId: string, result: 'pass' | 'fail' | 'timeout') => {
    setResultOverrides((prev) => ({ ...prev, [rowId]: result }));
  }, []);

  const handleMarkCase = useCallback((testCaseResultId: string, result: 'pass' | 'fail' | 'timeout') => {
    const caseRows = allFlatRows.filter((row) => row.testCaseResultId === testCaseResultId);
    setResultOverrides((prev) => {
      const next = { ...prev };
      caseRows.forEach((row) => { next[row.rowId] = result; });
      return next;
    });
  }, [allFlatRows]);

  // ─── Export handlers ────────────────────────────────────────────────────────

  const handleExportBtnClick = useCallback(() => {
    if (!exportBtnRef.current) return;
    const rect = exportBtnRef.current.getBoundingClientRect();
    setExportMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    setExportMenuOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!exportMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (
        exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node) &&
        exportBtnRef.current && !exportBtnRef.current.contains(e.target as Node)
      ) setExportMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExportMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [exportMenuOpen]);

  const handleExportFormat = useCallback((format: ReportFormat) => {
    setExportMenuOpen(false);
    if (!mission_id || !scenario_id || !selectedReportItemId) return;
    downloadTestReport(mission_id, scenario_id, selectedReportItemId, format);
  }, [mission_id, scenario_id, selectedReportItemId]);

  // ─── Scroll handlers ────────────────────────────────────────────────────────

  const handleScrollPosition = useCallback((
    scrollTop: number,
    clientHeight: number,
    scrollHeight: number,
    scrollDirection?: 'forward' | 'backward',
  ) => {
    isAtTopRef.current = scrollTop === 0;

    if (
      (scrollDirection === 'forward' || scrollDirection == null) &&
      hasMoreEntries && !loadingRef.current &&
      scrollHeight - clientHeight - scrollTop <= BOTTOM_LOAD_THRESHOLD
    ) {
      loadMoreEntries();
      return;
    }

    if ((scrollDirection === 'backward' || scrollDirection == null) && scrollTop === 0 && !loadingRef.current) {
      loadLatestEntries(false);
    }
  }, [hasMoreEntries, loadLatestEntries, loadMoreEntries]);

  const handleListScroll = useCallback(({ scrollDirection, scrollOffset }: { scrollDirection: 'forward' | 'backward'; scrollOffset: number }) => {
    if (isFrozen) return;
    isAtTopRef.current = scrollOffset === 0;
    const outerElement = listOuterRef.current;
    if (!outerElement) return;
    handleScrollPosition(outerElement.scrollTop, outerElement.clientHeight, outerElement.scrollHeight, scrollDirection);
  }, [handleScrollPosition, isFrozen]);

  const handleStaticScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    handleScrollPosition(element.scrollTop, element.clientHeight, element.scrollHeight);
  }, [handleScrollPosition]);

  // ─── Virtual list item data ─────────────────────────────────────────────────

  const itemData = useMemo<ReportsListItemData>(() => ({
    groups: groupedRows,
    onMarkRow: handleMarkRow,
    onMarkCase: handleMarkCase,
    selectedRowId,
    onSelectRow: handleSelectRow,
  }), [groupedRows, handleMarkRow, handleMarkCase, selectedRowId, handleSelectRow]);

  const messageTabItemData = useMemo(() => ({
    entries: selectedCommandEntries,
    selectedRow,
    getCommandDetails: noopGetCommandDetails,
    show_selected_output_details: showSelectedOutputDetails,
    consoleTableColumns: REPORTS_MESSAGE_COLUMNS,
    setIsOutputModalVisible,
    isOutputModalVisible,
    commandDetails,
    showCorrespondingTask: true,
    setIsFrozen: () => undefined,
    setDataCount: noopSetNumberState,
    listRef: messageListRef,
    height: 35,
    isFrozen: false,
    frozenData: null as ConsoleEntry[] | null,
    removeCommandUidForHighlight: () => undefined,
    highlightedCommands: new Set<string>(),
    dashboard,
    filteredColumns: REPORTS_MESSAGE_COLUMNS,
    testPlanResults: {},
    setTestPlanResults: noopSetTestPlanResults,
    setEntries: noopSetEntries,
    isValidateMode: false,
    readOnlyResults: true,
    pendingEntryResultsRef: { current: new Map<string, boolean>() },
    pendingGroupResultsRef: { current: new Map<string, boolean>() },
  }), [commandDetails, dashboard, isOutputModalVisible, selectedCommandEntries, selectedRow, showSelectedOutputDetails]);

  const shouldRenderVirtualList = groupedRows.length > 0 && listHeight > 0;

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (plansLoading) {
    return (
      <div className="rp__root">
        <aside className="rp__left"><ReportsLeftSkeleton /></aside>
        <main className="rp__right"><ReportsRightSkeleton /></main>
      </div>
    );
  }

  return (
    <div className="rp__root" data-testid="reports-container">
      <aside className="rp__left" data-testid="reports-left-panel">
        <div className="rp__left__label">Reports</div>

        <div className="rp__left__stats">
          <div className="rp__left__stats__count">
            <span className="rp__left__stats__text">Test Cases:</span>
            <span className="rp__left__stats__num">{sessionResult ? sessionResult.summary.total : totalStats.total}</span>
          </div>
          <div className="rp__left__stats__badges">
            <span className="rp__left__stats__badge rp__left__stats__badge--pass">
              <CheckIconSvg color="#CCF54E" />
              <span>{sessionResult ? sessionResult.summary.pass : totalStats.pass}</span>
            </span>
            <span className="rp__left__stats__badge rp__left__stats__badge--warn">
              <TriangleSvg color="#ff3333" />
              <span>{sessionResult ? sessionResult.summary.fail : totalStats.fail}</span>
            </span>
            {((sessionResult?.summary?.timeout ?? 0) > 0 || totalStats.timeout > 0) && (
              <span className="rp__left__stats__badge rp__left__stats__badge--timeout">
                <TimeoutIconSvg color="#FFA940" />
                <span>{sessionResult ? sessionResult.summary.timeout ?? 0 : totalStats.timeout}</span>
              </span>
            )}
            {(sessionResult?.summary?.cancelled ?? 0) > 0 && (
              <span className="rp__left__stats__badge rp__left__stats__badge--timeout" title="Cancelled / Aborted">
                <TimeoutIconSvg color="#FFA940" />
                <span>{sessionResult?.summary?.cancelled}</span>
              </span>
            )}
          </div>
        </div>

        <div className="rp__left__tabs" role="tablist" aria-label="Report result type filters">
          <button
            type="button" role="tab"
            aria-selected={activeLeftTab === 'plan'}
            className={`rp__left__tab${activeLeftTab === 'plan' ? ' rp__left__tab--active' : ''}`}
            onClick={() => handleLeftTabChange('plan')}
            disabled={leftPanelCounts.plan === 0}
            data-testid="reports-tab-plan"
          >
            <span>Test Plan</span>
          </button>
          <button
            type="button" role="tab"
            aria-selected={activeLeftTab === 'case'}
            className={`rp__left__tab${activeLeftTab === 'case' ? ' rp__left__tab--active' : ''}`}
            onClick={() => handleLeftTabChange('case')}
            disabled={leftPanelCounts.case === 0}
            data-testid="reports-tab-case"
          >
            <span>Test Case</span>
          </button>
        </div>

        <div className="rp__left__list">
          {visibleLeftPanelItems.map((item) => (
            <LeftPanelCard
              key={item.key}
              item={item}
              isActive={item.selectionId === selectedPlanId}
              onSelect={handleCardSelect}
            />
          ))}

          {visibleLeftPanelItems.length === 0 && (
            <div className="rp__left__empty">
              <span className="rp__left__empty__title">
                {activeLeftTab === 'plan' ? 'No test plans' : 'No test cases'}
              </span>
              <span className="rp__left__empty__text">
                Results for this session are not available in the selected tab yet.
              </span>
            </div>
          )}
        </div>
      </aside>

      <main className="rp__right" data-testid="reports-right-panel">
        {!selectedReportItemId ? (
          <div className="rp__center-msg">Select a test plan.</div>
        ) : (
          <>
            <div className="rp__right__header">
              <span className="rp__right__header__title">
                {selectedHeaderTitle.toUpperCase()}
              </span>
              <div className="rp__right__header__actions">
                {selectedLeftPanelItem && selectedLeftPanelItem.subsystems.length > 0 && (
                  <SubsystemChipsInline subsystems={selectedLeftPanelItem.subsystems} />
                )}
                <CustomToolTip title="Refresh Results" placement="top">
                  <button
                    className="right__panel__header__filters__icons"
                    type="button"
                    aria-label="Refresh Results"
                    onClick={refreshSessionResult}
                    disabled={sessionResultRefreshing}
                    data-testid="reports-refresh-btn"
                  >
                    <RefreshSvg />
                  </button>
                </CustomToolTip>
                <CustomToolTip title="Download File" placement="top">
                  <button
                    ref={exportBtnRef}
                    className="right__panel__header__filters__icons"
                    type="button"
                    aria-label="Download"
                    onClick={handleExportBtnClick}
                    data-testid="reports-download-btn"
                  >
                    <DownloadIcon />
                  </button>
                </CustomToolTip>
              </div>

              {exportMenuOpen && (
                <ExportMenu
                  menuRef={exportMenuRef}
                  pos={exportMenuPos}
                  onExport={handleExportFormat}
                />
              )}
            </div>



            <div className="rp__col-header">
              <div className="rp__col-header__cell rp__col-header__cell--dir"><TcTmCombinedIcon /></div>
              <div className="rp__col-header__cell rp__col-header__cell--time">Time</div>
              <div className="rp__col-header__cell rp__col-header__cell--id">TC/TM ID</div>
              <div className="rp__col-header__cell rp__col-header__cell--flex">Source</div>
              <div className="rp__col-header__cell rp__col-header__cell--flex">Destination</div>
              <div className="rp__col-header__cell rp__col-header__cell--result">Result</div>
            </div>

            <div ref={listContainerRef} className="rp__scroll-shell">
              {entriesLoading && (
                <div className="rp__shimmer-overlay">
                  <ReportsRightBodySkeleton />
                </div>
              )}

              {!entriesLoading && groupedRows.length === 0 && (
                <div className="rp__empty-state">
                  <div className="rp__empty-state__panel">
                    <div className="rp__empty-state__top">
                      <span className="rp__empty-state__eyebrow">Reports</span>
                      <span className="rp__empty-state__tag">Time Window</span>
                    </div>
                    <span className="rp__empty-state__title">No command details in this window</span>
                    <span className="rp__empty-state__text">
                      The selected result does not have any command entries in the current time window.
                    </span>
                    <span className="rp__empty-state__hint">
                      Try another result or refresh the results to load the latest data.
                    </span>
                  </div>
                </div>
              )}

              {shouldRenderVirtualList && (
                <List
                  ref={listRef}
                  outerRef={listOuterRef}
                  itemData={itemData}
                  height={listHeight}
                  itemCount={groupedRows.length}
                  itemSize={(index) => getSectionSize(groupedRows[index])}
                  width="100%"
                  onScroll={handleListScroll}
                  overscanCount={3}
                  className={`rp__scroll modal__scrollbar__white__vertical${isFrozen ? ' rp__scroll--frozen' : ''}`}
                >
                  {VirtualSection}
                </List>
              )}

              {groupedRows.length > 0 && !shouldRenderVirtualList && (
                <div
                  className={`rp__scroll modal__scrollbar__white__vertical${isFrozen ? ' rp__scroll--frozen' : ''}`}
                  onScroll={isFrozen ? undefined : handleStaticScroll}
                >
                  {groupedRows.map((group) => (
                    <div key={group.groupKey} className="rp__virtual-item">
                      <TestCaseSection
                        testPlanResultId={group.testPlanResultId}
                        testCaseId={group.testCaseId}
                        testCaseResultId={group.testCaseResultId}
                        testPlanName={group.testPlanName}
                        testCaseName={group.testCaseName}
                        hasGroupingMeta={group.hasGroupingMeta}
                        rows={group.rows}
                        onMarkRow={handleMarkRow}
                        onMarkCase={handleMarkCase}
                        selectedRowId={selectedRowId}
                        onSelectRow={handleSelectRow}
                      />
                    </div>
                  ))}
                </div>
              )}

              {loadingMore && groupedRows.length > 0 && (
                <div className="rp__loading-more">Loading…</div>
              )}
            </div>

            {isOutputModalVisible && (
              <div className="output__modal">
                <div className="output__modal__header__console">
                  <div className="output__modal__header__console__options__container">
                    <button
                      type="button"
                      className={`output__modal__header__console__option ${outputModalOptions.json ? 'active' : ''}`}
                      onClick={() => handleTabChange('json')}
                    >
                      CONTENTS
                    </button>
                    <button
                      type="button"
                      className={`output__modal__header__console__option ${outputModalOptions.messages ? 'active' : ''}`}
                      onClick={() => handleTabChange('messages')}
                    >
                      MESSAGES
                    </button>
                    <button
                      type="button"
                      className={`output__modal__header__console__option ${outputModalOptions.validations ? 'active' : ''}`}
                      onClick={() => handleTabChange('validations')}
                    >
                      VALIDATIONS
                    </button>
                  </div>
                  <div className="output__modal__header__console__actions_btns">
                    {hexString && foundPayload && (
                      <CustomToolTip title="Download Output" placement="top">
                        <button type="button" onClick={downloadOutputDetails}>
                          <DownloadIcon />
                        </button>
                      </CustomToolTip>
                    )}
                    <button type="button" onClick={removeSelection}>
                      <CloseIcon />
                    </button>
                  </div>
                </div>

                <div className="rp__output-modal__body modal__scrollbar__white__vertical">
                  <div className="right__panel__body__content__container right__panel__table__width modal__scrollbar__white__horizontal">
                    <div className="output__modal__body__container">
                      {outputModalOptions.json && (
                        <>
                          <Resizable
                            className="position-relative"
                            width={outputModalSize.width}
                            height={outputModalSize.height}
                            axis="x"
                            onResizeStop={onResizeStop}
                            onResizeStart={onResizeStart}
                            onResize={onResizeWidth}
                            handle={(handleAxis, ref) => (
                              <div
                                ref={ref}
                                className={`output__modal__left__resize__handler ${outputModalSize.resizing === 'width' ? 'active' : ''} handle-${handleAxis}`}
                              />
                            )}
                          >
                            <div>
                              <div
                                className="output__modal__left__section"
                                style={{ '--section-width': `${outputModalSize.width}px` } as React.CSSProperties}
                                onMouseDown={(event) => event.stopPropagation()}
                              >
                                <span>Packet Details:</span>
                                {commandDetailsLoading ? (
                                  <Loader />
                                ) : foundPayload ? (
                                  <JsonEditor
                                    value={JSON.stringify({ payload: foundPayload }, null, 2)}
                                    editable={false}
                                    backgroundColor="transparent"
                                  />
                                ) : (
                                  'Not available'
                                )}
                              </div>
                            </div>
                          </Resizable>

                          <div
                            className="output__modal__right__section"
                            onMouseDown={(event) => event.stopPropagation()}
                          >
                            <span>Raw Bytes:</span>
                            <div onMouseDown={(event) => event.stopPropagation()}>
                              {commandDetailsLoading ? <Loader /> : hexString || 'Not available'}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {outputModalOptions.messages && (
                      showNoMessage ? (
                        <div className="output__div__message__container">No associated message found.</div>
                      ) : (
                        <div
                          className={`right__panel__body__content__container right__panel__table__width modal__scrollbar__white__horizontal rp__messages-wrap${dashboard === 'operate' ? ' rp__messages-wrap--operate' : ''}`}
                        >
                          <FixedList
                            ref={messageListRef}
                            itemData={messageTabItemData}
                            height={35}
                            itemCount={1}
                            itemSize={35}
                            width="100%"
                          >
                            {ConsoleMessageRow}
                          </FixedList>
                        </div>
                      )
                    )}

                    {outputModalOptions.validations && (
                      <div className="output__validation__tab">
                        {validationDisplayRows.length > 0 ? (
                          <div className="output__validation__table">
                            <div className="output__validation__table__header">
                              <span>Parameters</span>
                              <span>Validations</span>
                              <span>Actual value</span>
                              <span>Results</span>
                            </div>
                            <div className="output__validation__table__body">
                              {validationDisplayRows.map((row) => {
                                const isFail = row.resultStatus?.toLowerCase().trim() === 'fail';
                                const isPass = row.resultStatus?.toLowerCase().trim() === 'pass';
                                const isTimeout =
                                  row.resultStatus?.toLowerCase().trim() === 'timeout' ||
                                  row.resultStatus?.toLowerCase().trim() === 'timed_out' ||
                                  row.resultStatus?.toLowerCase().trim() === 'timedout';
                                const rowTypeClass = isFail
                                  ? 'output__validation__table__row--fail'
                                  : isPass
                                  ? 'output__validation__table__row--pass'
                                  : isTimeout
                                  ? 'output__validation__table__row--timeout'
                                  : '';
                                const depthClass = row.depth === 0 ? 'rp__validation-table__row--root' : '';
                                const statusTone = getValidationStatusTone(row.resultStatus);
                                const actualText =
                                  row.resultValue != null && String(row.resultValue).trim() !== ''
                                    ? row.resultValue
                                    : '—';

                                return (
                                  <div
                                    key={row.key}
                                    className={`rp__validation-table__row output__validation__table__row ${rowTypeClass} ${depthClass}`}
                                    data-depth={row.depth}
                                  >
                                    <span className="output__validation__table__cell output__validation__table__cell--parameter">
                                      <ValidationParameterLabel
                                        depth={row.depth}
                                        label={row.parameter}
                                        nodeName={row.nodeName}
                                        nodeType={row.nodeType}
                                        fullPath={row.fullPath}
                                        hasChildren={row.hasChildren}
                                        isLastChild={row.isLastChild}
                                        ancestorRails={row.ancestorRails}
                                        hasChildFailure={row.hasChildFailure}
                                        status={row.resultStatus}
                                      />
                                    </span>
                                    <span className="output__validation__table__cell">
                                      {row.validations ?? ''}
                                    </span>
                                    <span className="output__validation__table__cell output__validation__table__cell--actual">
                                      {actualText}
                                    </span>
                                    <span className="output__validation__table__cell output__validation__table__cell--result">
                                      {row.resultStatus ? (
                                        <span className={`output__validation__status output__validation__status--${statusTone}`}>
                                          {statusTone === 'pass' ? <GreenTick /> : statusTone === 'fail' ? <RedWarning /> : (statusTone === 'timeout' || statusTone === 'cancelled') ? <OrangeTimeout /> : null}
                                          {row.resultStatus}
                                        </span>
                                      ) : (
                                        <span className="output__validation__table__result-value">—</span>
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="output__div__message__container">No data found.</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
